import { useMutation } from "@tanstack/react-query";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { toast } from "react-hot-toast";
import { z } from "zod";

import {
  openVIPDialog,
  setStripeRedirectUrl,
} from "@/context/redux/reducers/dialog/vip-dialog-slice";
import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useAppDispatch } from "@/hooks/redux-hooks";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

type VisitStripeChangePlanProps = {
  token: string;
  returnUrl: string;
  lang: Lang;
};

const visitStripeChangePlanSchema = z.object({
  data: z.object({
    sessionUrl: z.string(),
    isVip: z.boolean().nullish(),
  }),
  message: z.string(),
});

export type VisitStripeChangePlanData = z.infer<
  typeof visitStripeChangePlanSchema
>["data"];

export const visitStripeChangePlan = async ({
  token,
  returnUrl,
  lang,
}: VisitStripeChangePlanProps): Promise<VisitStripeChangePlanData> => {
  if (!returnUrl || !token) throw new Error("missing params");
  const endpoint = "/stripe/customer-portal-update-page";

  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User-Profile": token,
      "Accept-Language": lang,
    },
    body: JSON.stringify({ returnUrl }),
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return visitStripeChangePlanSchema.parse(await res.json()).data;
};

export const useVisitStripeChangePlan = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t, lang } = useLang();
  const { backendToken: token } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const duration = searchParams.get("duration");

  return useMutation(
    () =>
      visitStripeChangePlan({
        token: token ?? "",
        returnUrl: `${window.location.origin}${pathname}${
          duration ? `?duration=${duration}` : ""
        }`,
        lang,
      }),
    {
      onSuccess: ({ sessionUrl, isVip }) => {
        if (isVip) {
          dispatch(openVIPDialog());
          dispatch(setStripeRedirectUrl(sessionUrl));
        } else {
          if (!sessionUrl) {
            toast.error(t.error.serverError);
            return;
          }
          router.push(sessionUrl);
        }
      },
      onError: () => {
        toast.error(t.error.serverError);
      },
    }
  );
};
